import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

import { IconButton, Tab } from "@snapchat/snap-design-system";
import { ChevronDownIcon, ChevronUpIcon } from "@snapchat/snap-design-system-icons";
import { type MessageDescriptor, useIntl } from "react-intl";

import { MetricField } from "../../../gql-generated/types";
import { type ComputedMetricField, type InsightsMetricsMap, type StatsItem } from "../../models";
import { ChartSkeletonContainer } from "../ChartSkeletonContainer";
import { InsightsSubTabs } from "../InsightsSubTabs";
import { SubTabContent } from "../SubTabContent";
import { SubTabHeader } from "../SubTabHeader";
import { EventChartComponent } from "../charts";

interface EventMetric {
    label: MessageDescriptor;
    labelAverage?: MessageDescriptor;
    baseMetric: MetricField;
    averageMetric?: ComputedMetricField;
    tooltip?: MessageDescriptor;
    tooltipAverage?: MessageDescriptor;
    chartMetrics: InsightsMetricsMap<StatsItem>;
    chartMetricsComputed: InsightsMetricsMap<StatsItem>;
    compareWithTotalPlays: boolean;
    loading: boolean;
}

export const EventMetric: FC<EventMetric> = ({
    label,
    labelAverage,
    baseMetric,
    averageMetric,
    tooltip,
    tooltipAverage,
    chartMetrics,
    chartMetricsComputed,
    compareWithTotalPlays,
    loading,
}) => {
    const intl = useIntl();

    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        setSelectedIndex(chartMetrics[baseMetric]?.totalValueFormatted === "-" ? -1 : 0);
    }, [chartMetrics, baseMetric]);

    const handleCollapseClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setSelectedIndex(selectedIndex === -1 ? 0 : -1);
        },
        [selectedIndex],
    );

    const tabControls = useMemo(() => {
        return (
            <IconButton
                icon={selectedIndex === -1 ? <ChevronDownIcon size={24} /> : <ChevronUpIcon size={24} />}
                onClick={handleCollapseClick}
            />
        );
    }, [handleCollapseClick, selectedIndex]);

    return (
        <InsightsSubTabs selectedIndex={selectedIndex} onTabSelected={setSelectedIndex}>
            <Tab
                title={
                    <>
                        <SubTabHeader
                            label={intl.formatMessage(label)}
                            tooltip={tooltip ? intl.formatMessage(tooltip) : undefined}
                            statValue={chartMetrics[baseMetric]?.totalValueFormatted ?? ""}
                            diffValue={chartMetrics[baseMetric]?.diffValue}
                            loading={loading}
                        />
                        {!averageMetric && tabControls}
                    </>
                }
            >
                <SubTabContent>
                    <ChartSkeletonContainer skeletonActive={loading}>
                        <EventChartComponent
                            data={chartMetrics[baseMetric]}
                            totalPlays={chartMetrics[MetricField.LensPlays]}
                            compareWithTotalPlays={compareWithTotalPlays}
                        />
                    </ChartSkeletonContainer>
                </SubTabContent>
            </Tab>
            {averageMetric ? (
                <Tab
                    title={
                        <>
                            <SubTabHeader
                                label={labelAverage ? intl.formatMessage(labelAverage) : ""}
                                tooltip={tooltipAverage ? intl.formatMessage(tooltipAverage) : undefined}
                                statValue={chartMetricsComputed[averageMetric]?.valueOverTimeFormatted ?? ""}
                                diffValue={chartMetricsComputed[averageMetric]?.diffValue}
                                loading={loading}
                            />
                            {tabControls}
                        </>
                    }
                >
                    <SubTabContent>
                        <ChartSkeletonContainer skeletonActive={loading}>
                            <EventChartComponent data={chartMetricsComputed[averageMetric]} allowDecimals={true} />
                        </ChartSkeletonContainer>
                    </SubTabContent>
                </Tab>
            ) : undefined}
        </InsightsSubTabs>
    );
};
